import { Controller } from "@hotwired/stimulus";

// Lottie Animation Controller
//
// Renders Lottie animations from JSON data using lottie-web library
// Falls back gracefully if lottie-web is not installed
//
// Usage:
//   <div data-controller="lottie-animation"
//        data-lottie-animation-data-value="<%= animation_json.to_json %>">
//   </div>
//
// Values:
//   data - The Lottie animation JSON string (double-encoded for HTML attribute safety)
//   loop - Whether to loop the animation (default: true)
//   autoplay - Whether to autoplay (default: true)
//
// Note: Requires lottie-web package: npm install lottie-web

export default class extends Controller {
  static values = {
    data: String,
    loop: { type: Boolean, default: true },
    autoplay: { type: Boolean, default: true },
  };

  connect() {
    this.loadAnimation();
  }

  disconnect() {
    if (this.animation) {
      this.animation.destroy();
      this.animation = null;
    }
  }

  async loadAnimation() {
    if (!this.dataValue) {
      console.warn("Lottie animation: No data provided");
      return;
    }

    try {
      // Dynamic import for lottie-web
      const lottie = await import("lottie-web");

      // Parse the JSON data (it's double-encoded for HTML safety)
      const animationData = JSON.parse(this.dataValue);

      // Create the animation
      this.animation = lottie.default.loadAnimation({
        container: this.element,
        renderer: "svg",
        loop: this.loopValue,
        autoplay: this.autoplayValue,
        animationData: animationData,
      });

      // Add accessible attributes
      this.element.setAttribute("aria-busy", "false");
    } catch (error) {
      if (
        error.message.includes("Cannot find module") ||
        error.message.includes("Failed to fetch")
      ) {
        console.warn(
          "Lottie-web not installed. Run: npm install lottie-web",
          error,
        );
        this.showFallback("Animation requires lottie-web package");
      } else if (error instanceof SyntaxError) {
        console.error("Invalid Lottie JSON data:", error);
        this.showFallback("Invalid animation data");
      } else {
        console.error("Failed to load Lottie animation:", error);
        this.showFallback("Animation failed to load");
      }
    }
  }

  showFallback(message) {
    this.element.innerHTML = `
      <div class="bg-gray-100 rounded-lg p-8 text-center text-neutral-500">
        <svg class="w-12 h-12 mx-auto mb-2 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="text-sm">${message}</p>
      </div>
    `;
  }
}
